import { IconButton, Stack, Typography } from '@monorepo/nova-ui';
import { Collapse, InputBase } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { COLUMN_WIDTHS } from '../constants';

/**
 * Card wrapper for the trades table
 * No outer border on desktop — a right-side divider line separates it from the pinned card
 */
export const StyledTableCard = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.contrast,
  borderRight: `1px solid ${theme.palette.divider}`,
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',

  [theme.breakpoints.down('md')]: {
    flex: 'unset',
    width: '100%',
    borderRight: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

/**
 * Title bar row: section title on the left, search input on the right
 */
export const StyledTitleBar = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(4),
  paddingTop: 0,
  paddingLeft: 0,
  minHeight: theme.spacing(13),
  gap: theme.spacing(4),

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4, 5),
    minHeight: 'unset',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

/**
 * Search input wrapper with border and icon
 */
export const StyledSearchWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.customShape.borderRadiusFull,
  padding: theme.spacing(1, 2),
  width: theme.spacing(66.5), // ~266px

  [theme.breakpoints.down('md')]: {
    width: 'auto',
    flex: 1,
    maxWidth: theme.spacing(66.5),
  },
}));

/**
 * Search text input
 */
export const StyledSearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  color: theme.palette.text.secondary,

  '& input::placeholder': {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
}));

/**
 * Scrollable wrapper for horizontal scrolling on smaller screens
 */
export const StyledScrollableWrapper = styled(Stack)(({ theme }) => ({
  width: '100%',
  flex: 1,
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: 405, // 9 rows × 45px per row

  [theme.breakpoints.down('md')]: {
    maxHeight: 'none',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
      height: theme.spacing(1),
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.action.hover,
      borderRadius: theme.spacing(0.5),
    },
  },
}));

/**
 * Table container
 */
export const StyledTable = styled(Stack)(() => ({
  flexDirection: 'column',
  width: '100%',
  minWidth: 'fit-content',
}));

/**
 * Group header row (Positions | Volume | Metric3 spanning 2 cols each)
 */
export const StyledGroupHeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 0,
  paddingBottom: theme.spacing(0.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  justifyContent: 'flex-start',
}));

/**
 * Group header cell spanning 2 sub-columns
 */
export const StyledGroupHeaderCell = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 500,
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  // borderBottom: `1px solid ${theme.palette.divider}`,
}));

/**
 * Spacer for group header (aligns with status + symbol + direction + sentiment cols)
 */
export const StyledGroupHeaderSpacer = styled(Stack)(() => ({
  flexShrink: 0,
}));

/**
 * Sub-header row (Long | Short labels under each group)
 */
export const StyledHeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingBottom: theme.spacing(1),
}));

/**
 * Header cell
 *
 * leftPadding variants mirror the data cell padding so header labels sit
 * directly above their content:
 *   'chip' → theme.spacing(2) = 8px  (matches StyledChipCell padding-left)
 *   'data' → theme.spacing(1) = 4px  (matches StyledDataCell padding-left)
 *   'none' → 0  (default, for empty spacer cells)
 */
export const StyledHeaderCell = styled(Typography, {
  shouldForwardProp: (prop) =>
    prop !== 'isLeftAligned' && prop !== 'leftPadding',
})<{
  width?: string;
  isLeftAligned?: boolean;
  leftPadding?: 'chip' | 'data' | 'none';
}>(({ theme, width, isLeftAligned, leftPadding = 'none' }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: isLeftAligned ? 'flex-start' : 'center',
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 400,
  lineHeight: 1.4,
  color: theme.palette.text.third,
  whiteSpace: 'nowrap',
  padding: theme.spacing(1, 1.5),
  paddingLeft:
    leftPadding === 'chip'
      ? theme.spacing(2)
      : leftPadding === 'data'
        ? theme.spacing(1)
        : 0,

  ...(width === 'flex'
    ? {
        flex: 1,
        flexBasis: 0,
        minWidth: 0,
      }
    : {
        flexShrink: 0,
        width: width || 'auto',
      }),
}));

/**
 * Table body
 */
export const StyledTableBody = styled(Stack)(() => ({
  flexDirection: 'column',
}));

/**
 * Data row
 */
export const StyledRow = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{
  isActive?: boolean;
}>(({ theme, isActive }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(['background-color']),

  cursor: 'pointer',

  ...(isActive && {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.common.black
        : theme.palette.common.white,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    marginBottom: '-1px',
    position: 'relative',
    zIndex: 1,
  }),

  '&:hover': {
    backgroundColor: isActive
      ? theme.palette.mode === 'dark'
        ? theme.palette.common.black
        : theme.palette.common.white
      : alpha(theme.palette.action.hover, 0.04),
  },

  '&:last-child': {
    ...(!isActive && {
      borderBottom: 'none',
    }),
  },
}));

/**
 * Base cell
 */
const StyledCell = styled(Stack)<{
  width?: string;
  isLeftAligned?: boolean;
}>(({ width, isLeftAligned }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: isLeftAligned ? 'flex-start' : 'center',
  padding: 0,

  ...(width === 'flex'
    ? {
        flex: 1,
        flexBasis: 0,
        minWidth: 0,
      }
    : {
        flexShrink: 0,
        width: width || 'auto',
      }),
}));

/**
 * Status cell
 */
export const StyledStatusCell = styled(StyledCell)(() => ({
  width: COLUMN_WIDTHS.STATUS,
  justifyContent: 'center',
}));

/**
 * Symbol cell
 */
export const StyledSymbolCell = styled(StyledCell)(() => ({
  width: COLUMN_WIDTHS.SYMBOL,
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
}));

/**
 * Symbol text
 */
export const StyledSymbolText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  lineHeight: 1.5,
  color: theme.palette.text.secondary,

  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.pxToRem(13),
  },
}));

/**
 * Chip cell for direction/sentiment
 */
export const StyledChipCell = styled(StyledCell)(({ theme }) => ({
  width: COLUMN_WIDTHS.DIRECTION,
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  padding: theme.spacing(2.5, 2),
}));

/**
 * Data cell for numeric values
 */
export const StyledDataCell = styled(StyledCell)(({ theme }) => ({
  flex: 1,
  flexBasis: 0,
  minWidth: 0,
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  padding: theme.spacing(1.5, 1),
  width: '57.5px',
}));

/**
 * Data cell text
 */
export const StyledDataCellText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 400,
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

/**
 * N/A text for null values
 */
export const StyledNAText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 400,
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

/**
 * Pin cell
 */
export const StyledPinCell = styled(StyledCell)(({ theme }) => ({
  width: COLUMN_WIDTHS.PIN,
  justifyContent: 'center',
  padding: theme.spacing(1.5, 0.5),
}));

/**
 * Pin button
 */
export const StyledPinButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isPinned',
})<{ isPinned?: boolean }>(({ theme, isPinned }) => ({
  color: isPinned ? theme.palette.warning.main : theme.palette.text.secondary,
  width: theme.spacing(8),
  height: theme.spacing(8),
}));

const FLAG_SIZE = 20;
const FLAG_OVERLAP = 8;
const VERTICAL_OFFSET = 6;

const flagInnerStyles = {
  '& > *': {
    width: '100% !important',
    height: '100% !important',
    display: 'flex !important',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& svg': {
    width: '150% !important',
    height: '150% !important',
    flexShrink: 0,
  },
};

/**
 * Outer wrapper — reserves exact space for both flag circles with their diagonal offset.
 */
export const StyledFlagCircle = styled('div')({
  position: 'relative',
  width: FLAG_SIZE + FLAG_OVERLAP,
  height: FLAG_SIZE + VERTICAL_OFFSET,
  flexShrink: 0,
});

/**
 * Primary (base currency) flag circle — bottom-left, in front.
 */
export const StyledFlagPrimary = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: FLAG_SIZE,
  height: FLAG_SIZE,
  borderRadius: '50%',
  overflow: 'hidden',
  zIndex: 2,
  bottom: 0,
  left: 0,
  boxShadow: `0 0 0 2px ${theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.white}`,
  ...flagInnerStyles,
}));

/**
 * Secondary (quote currency) flag circle — top-right, behind.
 */
export const StyledFlagSecondary = styled('div')({
  position: 'absolute',
  width: FLAG_SIZE,
  height: FLAG_SIZE,
  borderRadius: '50%',
  overflow: 'hidden',
  zIndex: 1,
  top: 0,
  right: 0,
  ...flagInnerStyles,
});

/**
 * Status circle for pending state
 */
export const StyledStatusCircle = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isNumeric',
})<{ isNumeric?: boolean }>(({ theme, isNumeric }) => ({
  width: theme.spacing(8),
  height: theme.spacing(8),
  borderRadius: '50%',
  backgroundColor: isNumeric
    ? '#00A6DC'
    : theme.palette.mode === 'dark'
      ? theme.palette.common.black
      : theme.palette.common.white,
  alignItems: 'center',
  justifyContent: 'center',
}));

/**
 * Status circle abbreviation text
 */
export const StyledStatusAbbreviation = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isNumeric',
})<{ isNumeric?: boolean }>(({ theme, isNumeric }) => ({
  fontSize: theme.typography.pxToRem(10),
  fontWeight: 600,
  lineHeight: 1,
  color: isNumeric ? theme.palette.common.white : theme.palette.text.third,
  textTransform: 'uppercase',
  [theme.breakpoints.down('lg')]: {
    fontSize: theme.typography.pxToRem(9),
  },
}));

/**
 * Check icon wrapper for in-progress and completed states
 */
export const StyledCheckIconWrapper = styled(Stack)<{
  isCompleted?: boolean;
}>(({ theme, isCompleted }) => ({
  width: theme.spacing(8),
  height: theme.spacing(8),
  alignItems: 'center',
  justifyContent: 'center',
  color: isCompleted
    ? theme.palette.primary.main
    : theme.palette.text.secondary,
}));

/**
 * Table title
 */
export const StyledTableTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(20),
  fontWeight: 600,
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
}));

/**
 * Legend container at bottom of table — matches Figma "bottom sticky panel"
 */
export const StyledLegendContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(9.5), // ~38px gap between Long Bias and Short Bias
  padding: theme.spacing(1.75, 3.5), // ~7px vertical, 14px left
  borderTop: `1px solid ${theme.palette.divider}`,

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    gap: theme.spacing(4),
  },
}));

/**
 * Individual legend item
 */
export const StyledLegendItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

/**
 * Legend dot
 */
export const StyledLegendDot = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'dotColor',
})<{
  dotColor: 'long' | 'short';
}>(({ theme, dotColor }) => ({
  width: theme.spacing(2.5),
  height: theme.spacing(2.5),
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor:
    dotColor === 'long' ? theme.palette.warning.main : theme.palette.blue.main,
}));

/**
 * Legend label text
 */
export const StyledLegendText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 500,
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
}));

/**
 * Wrapper that hides the table header row on mobile (<900px)
 */
export const StyledTableHeaderWrapper = styled(Stack)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

/**
 * Mobile card row container — replaces StyledRow below 900px.
 * Active variant gets a primary border and elevation shadow.
 */
export const StyledMobileRow = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isExpanded',
})<{ isActive?: boolean; isExpanded?: boolean }>(
  ({ theme, isActive, isExpanded }) => ({
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    position: 'relative',
    transition: theme.transitions.create([
      'background-color',
      'box-shadow',
      'padding',
      'border',
      'border-radius',
    ]),
    backgroundColor: theme.palette.background.contrast,
    width: '100%',
    minWidth: 0,

    ...(isExpanded && {
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: theme.customShape.borderRadiusXLarge,
      padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
    }),

    '&:last-child': {
      ...(!isActive && {
        borderBottom: 'none',
      }),
    },
  }),
);

/**
 * Top section of mobile row — symbol + tags row
 */
export const StyledMobileTopSection = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  width: '100%',
  justifyContent: 'flex-start',
  // minWidth: 0,
}));

/**
 * Currency/symbol section — caret icon + symbol text
 */
export const StyledMobileCurrencySection = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  flexShrink: 1,
  minWidth: 0,
  // overflow: 'hidden',
}));

/**
 * Tags section — flex row containing label+tag groups
 */
export const StyledMobileTagsSection = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  // flex: 0.5,
  gap: theme.spacing(3),
  alignItems: 'center',
  flexWrap: 'wrap',
  minWidth: 0,
  minHeight: 0,
}));

/**
 * Individual label+tag group (e.g. "Positions" + Tag)
 */
export const StyledMobileTagGroup = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  flex: '1 1 auto',
  minWidth: 'max-content',
  alignItems: 'center',
  flexWrap: 'wrap',
  rowGap: theme.spacing(0.5),
  columnGap: theme.spacing(1.5),
  // overflow: 'hidden',
}));

/**
 * Small label above a tag (e.g. "Positions", "Volume")
 */
export const StyledMobileTagLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px !important',
  fontWeight: 400,
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

/**
 * Caret expand/collapse button for mobile row
 */
export const StyledMobileCaretButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded?: boolean }>(({ theme, isExpanded }) => ({
  color: theme.palette.text.secondary,
  width: theme.spacing(6),
  height: theme.spacing(6),
  flexShrink: 0,
  padding: 0,
  transition: theme.transitions.create('transform'),
  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
}));

/**
 * Pin button container positioned at top-right of mobile row
 */
export const StyledMobilePinButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isPinned',
})<{ isPinned?: boolean }>(({ theme, isPinned }) => ({
  color: isPinned ? theme.palette.warning.main : theme.palette.text.secondary,
  width: theme.spacing(8),
  height: theme.spacing(8),
  flexShrink: 0,
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(3),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.common.black
      : theme.palette.common.white,
  borderRadius: theme.spacing(4),
  padding: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

/**
 * Animated collapse wrapper for the mobile metrics section
 */
export const StyledMobileMetricsCollapse = styled(Collapse)(() => ({
  width: '100%',
}));

/**
 * Content wrapper for the expanded details section
 */
export const StyledExpandedContent = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
  padding: theme.spacing(2, 0, 0, 0),
  borderTop: `1px solid ${theme.palette.primary.main}`,
  marginTop: theme.spacing(1.5),
}));

/**
 * Loading container for the expanded details section
 */
export const StyledExpandedLoadingContent = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: theme.spacing(3, 0),
  borderTop: `1px solid ${theme.palette.primary.main}`,
  marginTop: theme.spacing(1.5),
}));

/**
 * Metrics section — three equal columns
 */
export const StyledMobileMetricsSection = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  width: '100%',
  gap: theme.spacing(1.5),
  fontSize: theme.typography.pxToRem(12),
  minWidth: 0,
  overflow: 'hidden',
  justifyContent: 'flex-end',
}));

/**
 * Individual metric column with optional right border divider
 */
export const StyledMobileMetricColumn = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'hasDivider',
})<{ hasDivider?: boolean }>(({ theme, hasDivider }) => ({
  flex: 1,
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  alignItems: 'flex-start',
  justifyContent: 'center',
  minWidth: 0,
  paddingRight: 0,
  paddingLeft: 0,
  overflow: 'hidden',

  ...(hasDivider && {
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(1.5),
  }),
}));

/**
 * Metric column header label (e.g. "Ave. Entry")
 */
export const StyledMobileMetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px !important',
  fontWeight: 400,
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
}));

/**
 * Sub-row inside a metric column (Long + Short side by side)
 */
export const StyledMobileMetricSubRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(0.5),
  width: '100%',
  minWidth: 0,
}));

/**
 * Single Long/Short entry within a metric column
 */
export const StyledMobileMetricEntry = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  flex: 1,
  minWidth: 0,
}));

/**
 * "Long" / "Short" sub-label
 */
export const StyledMobileMetricSubLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px !important',
  fontWeight: 400,
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

/**
 * Metric numeric value
 */
export const StyledMobileMetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '12px !important',
  fontWeight: 400,
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));
